import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import Card from '../../../../components/layouts/card/card';
import FontHeading2 from '../../../../components/fonts/font-heading2/font-heading2';
import FontBody1 from '../../../../components/fonts/font-body1/font-body1';
import { FONT_COLOR, FONT_VARIANT } from '../../../../components/fonts/font-props';
import { SelectDropdown, IOption } from '../../../../components/inputs/select-dropdown/select-dropdown';
import ErrorDistributionLine from '../error-distribution-line/error-distribution-line';
import healthDashboardStore from '../../health-dashboard-page-store';
import './error-distribution-table.scss';

interface IErrorDistributionTableProps {
    errorDistribution: any;
    isLoading?: boolean;
}

interface IErrorData {
    type: string;
    count: number;
    percentage?: number;
    resolved?: boolean;
}

const filterOptions: IOption[] = [
    { value: 'all', label: 'All Errors' },
    { value: 'unresolved', label: 'Unresolved Only' },
    { value: 'resolved', label: 'Resolved Only' }
];

const ErrorDistributionTable: React.FC<IErrorDistributionTableProps> = observer(({ 
    errorDistribution, 
    isLoading = false 
}) => {
    const [filterType, setFilterType] = useState<IOption>(filterOptions[0]);

    // Trigger API call when filter changes
    useEffect(() => {
        let isResolved: boolean | undefined;
        
        switch (filterType.value) {
            case 'resolved':
                isResolved = true;
                break;
            case 'unresolved':
                isResolved = false;
                break;
            case 'all':
            default:
                isResolved = undefined;
                break;
        }
        
        healthDashboardStore.fetchErrorDistribution(isResolved, healthDashboardStore.selectedDays);
    }, [filterType]);
    const parseErrorData = (data: any): IErrorData[] => {
        if (!data) return [];
        
        try {
            // Handle different possible data structures
            let errorList: any[] = [];
            
            if (data.payload?.list) {
                errorList = data.payload.list;
            } else if (data.data) {
                errorList = Array.isArray(data.data) ? data.data : [data.data];
            } else if (Array.isArray(data)) {
                errorList = data;
            } else if (typeof data === 'object') {
                // Convert object to array of key-value pairs
                errorList = Object.entries(data).map(([key, value]) => ({
                    type: key,
                    count: typeof value === 'number' ? value : 0
                }));
            }
            
            // Calculate total for percentages
            const total = errorList.reduce((sum, item) => {
                const count = item.count || item.value || 0;
                return sum + count;
            }, 0);
            
            // Transform to consistent format
            return errorList.map((item) => {
                const count = item.count || item.value || 0;
                return {
                    type: item.type || item.error_type || item.name || 'Unknown Error',
                    count: count,
                    percentage: total > 0 ? (count / total) * 100 : 0,
                    resolved: item.resolved || item.is_resolved || false
                };
            })
            .filter(item => item.count > 0) // Only show errors with counts > 0
            .sort((a, b) => b.percentage - a.percentage); // Sort by percentage descending (highest first)
            
        } catch (error) {
            console.error('Error parsing error distribution data:', error);
            return [];
        }
    };

    const allErrorData = parseErrorData(errorDistribution);
    const hasData = allErrorData.length > 0;
    
    const handleErrorClick = (errorType: string) => {
        // Navigate to specific error view - this could be implemented based on your routing structure
        console.log(`Navigating to error details for: ${errorType}`);
        // Example: window.location.href = `/admin/errors/${encodeURIComponent(errorType)}`;
    };

    return (
        <Card isLoading={isLoading}>
            <div className="error-distribution-header">
                <div className="header-content">
                    <FontHeading2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY3}>
                        Error Distribution
                    </FontHeading2>
                    <FontBody1 color={FONT_COLOR.NEUTRAL12}>
                        Overview of error types and their frequency.<br/>
                        Note that a single RMA may have multiple errors.
                    </FontBody1>
            </div>
            <div className="filter-controls">
                <SelectDropdown
                    value={filterType}
                    options={filterOptions}
                    onChange={(selectedOption) => {
                        if (selectedOption) {
                            setFilterType(selectedOption);
                        }
                    }}
                    placeholder="Filter errors"/>
            </div>
        </div>
            <div className="error-distribution-content">
                {!isLoading && (
                    <>
                        {hasData ? (
                            allErrorData.map((error, index) => (
                                <ErrorDistributionLine
                                    key={`${error.type}-${index}`}
                                    errorType={error.type}
                                    count={error.count}
                                    percentage={error.percentage}
                                    onClick={() => handleErrorClick(error.type)}/>
                            ))
                        ) : (
                            <div className="no-data-message">
                                <FontBody1 color={FONT_COLOR.NEUTRAL3}>
                                    {filterType.value === 'all'
                                        ? 'No error data available'
                                        : `No ${filterType.label.toLowerCase()} found`}
                                </FontBody1>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Card>
    );
});

export default ErrorDistributionTable;
